import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { convertBytesToKiloBytes } from 'utils';
import { CompanyApi, DocumentApi, MiscellaneousApi, StepApi } from 'services';
import { find, get, map, isArray, isEmpty, isNil } from 'lodash';
import { toastMessages } from 'config/constants';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';
import useLocalStorage from 'hooks/useLocalStorage';

const initialCompanyDetails = {
  description: '',
  link: '',
  main_picture: '',
  pictures: [],
};

const CompanyContext = createContext(undefined);
const CompanyFunctionsContext = createContext(undefined);

function CompanyProvider({ children }) {
  const [companies, setCompanies] = useLocalStorage('companies', []);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companyDetailsLoading, setCompanyDetailsLoading] = useState(true);
  const [companyStepLoading, setCompanyStepLoading] = useState(true);
  const [companiesSaving, setCompaniesSaving] = useState(false);
  const [currentCompany, setCurrentCompany] = useLocalStorage('company', null);
  const [currentCompanyDetails, setCurrentCompanyDetails] = useState(initialCompanyDetails);
  const [companySteps, setCompanySteps] = useState(null);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await CompanyApi.listCompanies();

      const companyList = data.companies.map((company) => {
        const activity = data.activities.find((a) => a.company_id === company.id);
        const address = data.addresses.find((a) => a.company_id === company.id);

        return {
          ...company,
          activity: activity?.name,
          address_name: address.address_name,
          bankURL: company?.bankURL ?? null,
          category: '',
          city_name: address.city_name,
          identityURL: company?.identityURL ?? null,
          insuranceURL: company?.insuranceURL ?? null,
          kbisURL: company?.kbisURL ?? null,
          street_index: address.street_index,
          street_number: address.street_number,
          subcategory: '',
          way_type: address.way_type,
          zip_code: address.zip_code,
        };
      });

      setCompanies(companyList);
      if (isEmpty(companyList)) return;

      setCurrentCompany(find(companyList, { id: currentCompany?.id }) || companyList[0]);
    } catch (e) {
      if (get(e, 'response.status') !== 401) toast.error(toastMessages.error.FETCH_COMPANIES);
    }

    setCompaniesLoading(false);
  }, [currentCompany?.id, setCompanies, setCurrentCompany]);

  const loadSteps = useCallback(async () => {
    if (!currentCompany?.id) return;

    setCompanyStepLoading(true);
    try {
      const { data } = await StepApi.getSteps(currentCompany?.id);
      setCompanySteps(data);
    } catch (e) {
      if (get(e, 'response.status') !== 401) toast.error(toastMessages.error.FETCH_STEPS);
    } finally {
      setCompanyStepLoading(false);
    }
  }, [currentCompany?.id]);

  async function loadCompanyDetails(companyId) {
    try {
      const { company, pictures } = await CompanyApi.getCompanyDetails(companyId);

      setCurrentCompanyDetails({
        description: company?.description || '',
        link: company?.link || '',
        main_picture: company?.main_picture || '',
        max_data_size: company?.max_data_size || 20480,
        nb_max_pictures: company?.nb_max_pictures || 8,
        pictures: pictures || [],
      });
    } catch (e) {
      toast.error(toastMessages.error.FETCH_COMPANY_DETAILS);
    }
    setCompanyDetailsLoading(false);
  }

  const changeCurrentCompany = useCallback(
    (companyId) => {
      const company = find(companies, { id: companyId });
      if (isNil(company)) return;

      setCurrentCompany(company);
    },
    [companies, setCurrentCompany],
  );

  useEffect(() => {
    loadCompanies();
    loadSteps();
  }, [loadCompanies, loadSteps]);

  useEffect(() => {
    if (currentCompany?.id && !companiesLoading) {
      setCompanyDetailsLoading(true);
      loadCompanyDetails(currentCompany?.id);
    }
  }, [companiesLoading, currentCompany?.id]);

  const createCompany = useCallback(
    async (company, data, documentData) => {
      setCompaniesSaving(true);

      try {
        const response = await CompanyApi.createCompany(data);
        let newCompany = { ...company, id: response.data.id };

        let docUpdated = false;
        let error = false;
        if (documentData) {
          const { data, name, url } = documentData;
          try {
            await DocumentApi.updateCompanyDocument(newCompany.id, name, data);
            newCompany = { ...newCompany, [`${name}URL`]: url };
            docUpdated = true;
          } catch (e) {
            error = true;
          }
        }

        const newCompanies = [...companies, newCompany];
        setCompanies(newCompanies);
        setCompaniesSaving(false);

        setTimeout(loadCompanies);

        toast.success(toastMessages.success.CREATE_COMPANY);
        if (docUpdated) toast.success(toastMessages.success.UPDATE_DOCUMENT);
        else if (error) toast.error(toastMessages.error.UPDATE_DOCUMENT);

        return { created: true, documentUpdated: docUpdated, item: newCompany };
      } catch (e) {
        toast.error(toastMessages.error.CREATE_COMPANY);
        return { created: false };
      }
    },
    [companies, loadCompanies, setCompanies],
  );

  const deleteCompany = useCallback(
    async ({ id }) => {
      setCompaniesSaving(true);

      try {
        await CompanyApi.deleteCompany(id);

        const newCompanyList = companies.filter((c) => c.id !== id);
        setCompanies(newCompanyList);

        if (currentCompany.id === id) {
          const newCurrentComp = newCompanyList.find((c) => c.id !== id);
          setCurrentCompany(newCurrentComp);
        }
        setCompaniesSaving(false);

        setTimeout(loadCompanies);

        toast.success(toastMessages.success.DELETE_COMPANY);
        return true;
      } catch (e) {
        setCompaniesSaving(false);
        toast.error(toastMessages.error.DELETE_COMPANY);
        return false;
      }
    },
    [companies, currentCompany?.id, loadCompanies, setCompanies, setCurrentCompany],
  );

  const editCompany = useCallback(
    async (company, data, documentData) => {
      setCompaniesSaving(true);

      try {
        await CompanyApi.updateCompany(company.id, data);
        let newCompany = { ...company, ...data };

        let docUpdated = false;
        let error = false;
        if (!isNil(documentData) && (!isArray(documentData) || !isEmpty(documentData))) {
          try {
            const documents = isArray(documentData) ? documentData : [documentData];
            await Promise.all(
              map(documents, async (doc) => {
                await DocumentApi.updateCompanyDocument(company.id, doc.name, doc.data);
                newCompany = { ...newCompany, [`${doc.name}Url`]: doc.url };
              }),
            );
            docUpdated = true;
          } catch (e) {
            error = true;
          }
        }

        const index = companies.findIndex((c) => c.id === company.id);
        let newItems = [...companies];
        newItems.splice(index, 1, newCompany);
        setCompanies(newItems);
        if (currentCompany.id === company.id) setCurrentCompany(newCompany);
        setCompaniesSaving(false);

        setTimeout(loadCompanies);

        toast.success(toastMessages.success.UPDATE_COMPANY);
        if (docUpdated) toast.success(toastMessages.success.UPDATE_DOCUMENT);
        else if (error) toast.error(toastMessages.error.UPDATE_DOCUMENT);

        return { edited: true, documentUpdated: true, item: newCompany };
      } catch (e) {
        setCompaniesSaving(false);
        toast.error(toastMessages.error.UPDATE_COMPANY);
        return { edited: false, documentUpdate: false };
      }
    },
    [companies, currentCompany, loadCompanies, setCompanies, setCurrentCompany],
  );

  const editCompanyDetails = useCallback(async (company, data) => {
    let body = {
      description: data.description,
      linkUrl: data.link,
      mainPicture: data.mainPicture,
      picturesToAdd: data.picturesToAdd?.map((p) => ({ name: p.name, size: convertBytesToKiloBytes(p.size) })) ?? [],
      picturesToRemove: data.picturesToRemove ?? [],
      step: 1,
    };

    try {
      let result = await CompanyApi.updateCompanyDetails(company.id, body);

      if (body.picturesToAdd.length && result.status !== 'UPLOAD_OK') {
        toast.error(toastMessages.error[result.status]);
        return false;
      }

      if (result.status === 'UPLOAD_OK') {
        const picturesToAdd = [];
        for (const picture of result.pictures) {
          await MiscellaneousApi.uploadFileToS3(
            picture.signedUrl,
            data.picturesToAdd.find((p) => p.name === picture.name),
          );
          picturesToAdd.push({
            name: picture.name,
            image_key: picture.image_key,
            company_id: company.id,
            size: picture.size,
          });
        }

        body = { ...body, picturesToAdd, step: 2 };
        result = await CompanyApi.updateCompanyDetails(company.id, body);
      }

      setCurrentCompanyDetails({
        description: data.description,
        link: data.link,
        main_picture: data.mainPicture,
        pictures: result.pictures,
      });
      toast.success(toastMessages.success.UPDATE_COMPANY_DETAILS);
      return result;
    } catch (e) {
      toast.error(toastMessages.error.UPDATE_COMPANY_DETAILS);
      return false;
    }
  }, []);

  const editCompanyDocument = useCallback(
    async (companyId, documentName, data, fileURL) => {
      setCompaniesSaving(true);

      try {
        await DocumentApi.updateCompanyDocument(companyId, documentName, data);

        const index = companies.findIndex((c) => c.id === companyId);
        let newItems = [...companies];
        const newCompany = { ...newItems[index], [`${documentName}URL`]: fileURL };
        newItems.splice(index, 1, newCompany);
        setCompanies(newItems);
        if (currentCompany.id === companyId) setCurrentCompany(newCompany);

        setCompaniesSaving(false);
        toast.success(toastMessages.success.UPDATE_DOCUMENT);
        return true;
      } catch (e) {
        setCompaniesSaving(false);
        toast.error(toastMessages.error.UPDATE_DOCUMENT);
        return false;
      }
    },
    [companies, currentCompany, setCompanies, setCurrentCompany],
  );

  const value = useMemo(
    () => ({
      companies,
      companiesLoading,
      companiesSaving,
      companyDetailsLoading,
      currentCompany,
      currentCompanyDetails,
      companySteps,
      companyStepLoading,
    }),
    [
      companies,
      companiesLoading,
      companiesSaving,
      companyDetailsLoading,
      currentCompany,
      currentCompanyDetails,
      companySteps,
      companyStepLoading,
    ],
  );

  const functions = useMemo(
    () => ({
      changeCurrentCompany,
      createCompany,
      deleteCompany,
      editCompany,
      editCompanyDetails,
      editCompanyDocument,
      setCompanies,
      setCurrentCompany,
      loadSteps,
    }),
    [
      changeCurrentCompany,
      createCompany,
      deleteCompany,
      editCompany,
      editCompanyDetails,
      editCompanyDocument,
      setCompanies,
      setCurrentCompany,
      loadSteps,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>
      <CompanyFunctionsContext.Provider value={functions}>{children}</CompanyFunctionsContext.Provider>
    </CompanyContext.Provider>
  );
}

/**
 * Return the items values.
 * @return {{
 *  companies: Array,
 *  companiesLoading: boolean,
 *  companiesSaving: boolean,
 *  currentCompany: Object,
 *  currentCompanyDetails: {description: string, link: string},
 * }}
 */
function useCompany() {
  return useGenericContext(CompanyContext, 'useCompany cannot be used outside of a CompanyProvider component.');
}

/**
 * Return the functions manipulating items.
 * @return {{ editCompany: Function }}
 */
function useCompanyFunctions() {
  return useGenericContext(
    CompanyFunctionsContext,
    'useCompanyFunctions cannot be used outside of a CompanyProvider component',
  );
}

export { CompanyProvider, useCompany, useCompanyFunctions };
